import { ChangeDetectionStrategy, Component } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import {
	buildTs1PreviewCandidateDetails,
	Ts1PreviewCandidateDetails,
} from "../ts1-rating-form-v3/preview/ts1-preview-summary-details.utils";
import {
	collectEts1SelectedSessionCodes,
	extractEts1SessionConfigurationsFromCandidate,
	flattenCandidateSessionDetailCodes,
	resolveEts1SessionGroupCode,
} from "src/app/cbta-web/ets1/session/ets1-session-selection.utils";
import { MatStepper } from "@angular/material/stepper";
import {
	buildEts1SessionColumnTree,
	Ets1SessionColumnVM,
} from "src/app/cbta-web/ets1/session/ets1-session-layout.utils";
import {
	isEts1SessionV2,
	mapApiRowsToEts1SessionConfig,
	patchEts1SessionSelectionFromTemplate,
	resolveEts1SessionAocCode,
} from "src/app/cbta-web/ets1/session/ets1-session-form.utils";
import {
	hasMissingRequiredEts1RadioSelection,
	hasMissingRequiredSessionText,
	hasSessionRemarkRequiredErrors,
	markMissingRequiredEts1RadioSelection,
	missingRequiredSessionText,
} from "src/app/cbta-web/ets1/session/ets1-session-validation.utils";
import { parseFormConfigResponse } from "src/app/cbta-web/forms/ts1-v2/ts1-form.factories";
import { DraftItem, StartRatingForm, TS1FormResponseJSON } from "src/app/models/models";
import { Ts1RatingFormV3Component } from "../ts1-rating-form-v3/ts1-rating-form-v3.component";

/**
 * TS-1 v4 rating shell — own template; reuses v3 orchestration until decomposed.
 * Calculation dispatches on `ratingFormVersion === 4` via ts1-rating-form.registry.ts.
 */
@Component({
	selector: "app-ts1-rating-form-v4",
	templateUrl: "./ts1-rating-form-v4.component.html",
	styleUrls: ["./ts1-rating-form-v4.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1RatingFormV4Component extends Ts1RatingFormV3Component {
	protected override readonly useEts1AocSessions: boolean = true;

	ets1SessionColumnTree: Ets1SessionColumnVM[] = [];

	get isEts1AocLayout(): boolean {
		return this.useEts1AocSessions && isEts1SessionV2({ ets1SessionVersion: this.ets1SessionVersion });
	}

	override loadForm(
		resp: TS1FormResponseJSON,
		manuver: Array<{ name?: string; observation_category?: { code?: string } }> = [],
		session: any[] = [],
	): void {
		super.loadForm(resp, manuver, session);
		if (!isEts1SessionV2(this.rawFormData)) {
			this.ets1SessionColumnTree = [];
			return;
		}

		const aocCode = resolveEts1SessionAocCode(
			this.rawFormData ?? resp,
			this.roleService.getUserAOC(),
		);
		if (!aocCode) {
			this.ets1SessionColumnTree = [];
			return;
		}

		// Match admin: show the full AOC catalog for the form's AOC (all 7: maa, aax, taa, tax, iaa, paa, aac).
		this.httpService.getFormConfig(resp.formType, 2, aocCode).subscribe((payload) => {
			const { sessionConfigurations } = parseFormConfigResponse(payload);
			const catalog = mapApiRowsToEts1SessionConfig(sessionConfigurations);
			this.ets1SessionColumnTree = buildEts1SessionColumnTree(catalog);
			this.cd.detectChanges();
		});
	}

	get isStep1StartRatingDisabled(): boolean {
		return this.ts1TrainingInfoForm.invalid || this.hasEts1MissingRequiredSessionText;
	}

	get hasEts1MissingRequiredSessionText(): boolean {
		return this.findEts1MissingRequiredSessionText(false);
	}

	get showEts1SessionRemarkRequiredSummary(): boolean {
		return this.hasEts1MissingRequiredSessionText || this.hasEts1SessionRemarkRequiredErrors;
	}

	get hasEts1SessionRemarkRequiredErrors(): boolean {
		if (!isEts1SessionV2(this.rawFormData) || !this.ets1SessionColumnTree.length) {
			return false;
		}

		const groupCode = this.sessionKeys[0]?.name?.code ?? "ts_1";
		return this.candidates.controls.some((candidate) => {
			const sessionRemarks = (candidate.get("sessionRemarks") as UntypedFormGroup | null)?.get(groupCode) as
				| UntypedFormGroup
				| null;
			return !!sessionRemarks && hasSessionRemarkRequiredErrors(sessionRemarks);
		});
	}

	override getStartRatingData(): StartRatingForm {
		const data = super.getStartRatingData();
		if (!this.isEts1AocLayout) return data;

		const groupCode = resolveEts1SessionGroupCode(this.sessionKeys);
		return {
			...data,
			candidates: data.candidates.map((candidate, index) => {
				const control = this.candidates.at(index) as UntypedFormGroup | null;
				const sessionsGroup = (control?.get("sessionDetails") as UntypedFormGroup | null)?.get(
					groupCode,
				) as UntypedFormGroup | null;
				return {
					...candidate,
					sessionDetails: collectEts1SelectedSessionCodes(sessionsGroup),
				};
			}),
		};
	}

	protected override loadDraftTrainingInfo(draft: DraftItem): void {
		super.loadDraftTrainingInfo(draft);
		if (!this.isEts1AocLayout || !this.ets1SessionColumnTree.length) return;

		const groupCode = resolveEts1SessionGroupCode(this.sessionKeys);
		const trainingInfo = draft.trainingInformation;

		(this.candidates.controls as UntypedFormGroup[]).forEach((candidateControl, index) => {
			const stored = trainingInfo.candidates?.[index];
			if (!stored) return;

			const sessionsGroup = (candidateControl.get("sessionDetails") as UntypedFormGroup | null)?.get(
				groupCode,
			) as UntypedFormGroup | null;
			const sessionRemarksGroup = (candidateControl.get("sessionRemarks") as UntypedFormGroup | null)?.get(
				groupCode,
			) as UntypedFormGroup | null;
			if (!sessionsGroup || !sessionRemarksGroup) return;

			const selectedCodes = flattenCandidateSessionDetailCodes(stored.sessionDetails);
			const remarksStored = Array.isArray(stored.sessionRemarks)
				? (stored.sessionRemarks[0] as Record<string, string>)
				: ((stored.sessionRemarks as Record<string, string> | undefined) ?? {});

			patchEts1SessionSelectionFromTemplate(
				sessionsGroup,
				sessionRemarksGroup,
				selectedCodes,
				remarksStored,
				this.ets1SessionColumnTree,
			);
		});
	}

	override async onStepperNext(stepper: MatStepper): Promise<void> {
		if (this.findEts1MissingRequiredSessionText(true) || this.findEts1MissingRequiredRadioSelection(true)) {
			this.gotoTop();
			return;
		}
		await super.onStepperNext(stepper);
	}

	private findEts1MissingRequiredSessionText(markErrors: boolean): boolean {
		if (!isEts1SessionV2(this.rawFormData) || !this.ets1SessionColumnTree.length) {
			return false;
		}

		const groupCode = this.sessionKeys[0]?.name?.code ?? "ts_1";
		let missing = false;

		for (const candidate of this.candidates.controls) {
			const sessions = (candidate.get("sessionDetails") as UntypedFormGroup | null)?.get(groupCode) as
				| UntypedFormGroup
				| null;
			const sessionRemarks = (candidate.get("sessionRemarks") as UntypedFormGroup | null)?.get(groupCode) as
				| UntypedFormGroup
				| null;
			if (!sessions || !sessionRemarks) continue;

			const candidateMissing = markErrors
				? missingRequiredSessionText(sessions, sessionRemarks, this.ets1SessionColumnTree)
				: hasMissingRequiredSessionText(sessions, sessionRemarks, this.ets1SessionColumnTree);
			if (candidateMissing) missing = true;
		}

		if (missing && markErrors) {
			this.cd.detectChanges();
		}

		return missing;
	}

	private findEts1MissingRequiredRadioSelection(markErrors: boolean): boolean {
		if (!isEts1SessionV2(this.rawFormData) || !this.ets1SessionColumnTree.length) {
			return false;
		}

		const groupCode = resolveEts1SessionGroupCode(this.sessionKeys);
		let missing = false;

		for (const candidate of this.candidates.controls) {
			const sessions = (candidate.get("sessionDetails") as UntypedFormGroup | null)?.get(groupCode) as
				| UntypedFormGroup
				| null;
			if (!sessions) continue;

			const candidateMissing = markErrors
				? markMissingRequiredEts1RadioSelection(sessions, this.ets1SessionColumnTree)
				: hasMissingRequiredEts1RadioSelection(sessions, this.ets1SessionColumnTree);
			if (candidateMissing) missing = true;
		}

		if (missing && markErrors) {
			this.cd.detectChanges();
		}

		return missing;
	}

	override readonly previewCandidateDetailsForIndex = (index: number): Ts1PreviewCandidateDetails | null =>
		this.buildEts1PreviewCandidateDetailsForIndex(index);

	private buildEts1PreviewCandidateDetailsForIndex(index: number): Ts1PreviewCandidateDetails | null {
		const ctrl = this.candidates.at(index) as UntypedFormGroup | null;
		if (!ctrl) return null;

		const details = buildTs1PreviewCandidateDetails(ctrl, this.sessionKeys, this.syllabusStartDate.value);
		if (!this.isEts1AocLayout || !this.ets1SessionColumnTree.length) {
			return details;
		}

		return {
			...details,
			sessionConfigurations: extractEts1SessionConfigurationsFromCandidate(
				ctrl,
				this.ets1SessionColumnTree,
				resolveEts1SessionGroupCode(this.sessionKeys),
			),
		};
	}
}
